import Heading from '@atlaskit/heading';
import { Inline, Stack } from '@atlaskit/primitives';
import { getDatabaseContainerType, useAnalytics } from '@databases/analytics';
import { defaultStoreUtils } from '@databases/redux';
import { DatabaseField } from '@databases/types/database';
import { DatabaseLayoutType, shouldShowField } from '@databases/types/database/view';
import {
  ViewChangeType,
  ViewChangeTypes,
  getGroupField,
  getDateField,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import useView from '../../hooks/useView';
import { messages } from '../../i18n';
import FieldsSearch from '../FieldsSearch';
import TextButton from '../TextButton';

import FieldOption from './FieldOption';

export interface ViewSettingsFieldsProps {
  contentId: string;
  databaseFields: DatabaseField[];
}

const ViewSettingsFields: React.FunctionComponent<ViewSettingsFieldsProps> = ({
  contentId,
  databaseFields,
}) => {
  const client = useAnalytics();
  const { view, setViewChange } = useView();
  const { formatMessage } = useIntl();
  const viewSettingsFields = view.settings.fields;
  const layout = view.settings.layout;

  const [searchValue, setSearchValue] = useState('');

  const database = defaultStoreUtils.useEqualSelector((state) => {
    return contentId
      ? selectDatabaseByContentIdRequired(state, {
          contentId,
        })
      : undefined;
  });

  const groupField = database && getGroupField(database, layout);
  const dateField = database && getDateField(database, layout);

  const handleToggleField = useCallback(
    (field: DatabaseField) => {
      const hidden = shouldShowField(viewSettingsFields, field.id);
      const viewChange: ViewChangeType = {
        type: ViewChangeTypes.VIEW_FIELDS,
        fieldKey: field.id,
        fieldValue: !hidden,
        fields: databaseFields,
      };
      setViewChange(viewChange);

      client.sendHideColumnsEvent({
        source: 'databaseViewSettingsPanel',
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        hidden,
        fieldType: field.type,
      });
    },
    [client, contentId, databaseFields, setViewChange, viewSettingsFields]
  );

  const filteredFields = databaseFields
    .filter(
      (field) => layout.type !== DatabaseLayoutType.BOARD || field.id !== groupField?.id
    )
    .filter(
      (field) => layout.type !== DatabaseLayoutType.CALENDAR || field.id !== dateField?.id
    );

  const shownFields = useMemo(
    () => filteredFields.filter((field) => shouldShowField(viewSettingsFields, field.id)),
    [filteredFields, viewSettingsFields]
  );

  const hiddenFields = useMemo(
    () =>
      filteredFields.filter((field) => !shouldShowField(viewSettingsFields, field.id)),
    [filteredFields, viewSettingsFields]
  );

  const handleToggleAllFields = useCallback(
    (value: boolean) => {
      const viewChanges: ViewChangeType[] = [];
      for (const field of filteredFields) {
        if (value !== shouldShowField(viewSettingsFields, field.id)) {
          viewChanges.push({
            type: ViewChangeTypes.VIEW_FIELDS,
            fieldKey: field.id,
            fieldValue: value,
            fields: databaseFields,
          } as ViewChangeType);
        }
      }

      setViewChange(viewChanges);

      client.sendHideAllColumnsEvent({
        source: 'databaseViewSettingsPanel',
        contentId: contentId,
        databaseContainer: getDatabaseContainerType(),
        hidden: !value, // We send the opposite value because we are sending the action to hide the columns
      });
    },
    [client, contentId, databaseFields, filteredFields, setViewChange, viewSettingsFields]
  );

  return (
    <Stack space="space.150">
      <FieldsSearch searchValue={searchValue} setSearchValue={setSearchValue} />
      {shownFields.length > 0 && (
        <>
          <Inline alignBlock="center" spread="space-between">
            <Heading as="h3" size="xsmall">
              {formatMessage(messages['view-settings-fields.heading.shown'])}
            </Heading>
            <TextButton
              text={formatMessage(messages['view-settings-fields.switch.hide-all'])}
              onClick={() => handleToggleAllFields(false)}
            />
          </Inline>
          <Stack space="space.100">
            {shownFields
              .filter((field) =>
                field.name.toLowerCase().includes(searchValue.toLowerCase())
              )
              .map((field) => (
                <FieldOption
                  key={field.id}
                  field={field}
                  handleToggleField={handleToggleField}
                />
              ))}
          </Stack>
        </>
      )}
      {hiddenFields.length > 0 && (
        <>
          <Inline alignBlock="center" spread="space-between">
            <Heading as="h3" size="xsmall">
              {formatMessage(messages['view-settings-fields.heading.hidden'])}
            </Heading>
            <TextButton
              text={formatMessage(messages['view-settings-fields.switch.show-all'])}
              onClick={() => handleToggleAllFields(true)}
            />
          </Inline>
          <Stack space="space.100">
            {hiddenFields
              .filter((field) =>
                field.name.toLowerCase().includes(searchValue.toLowerCase())
              )
              .map((field) => (
                <FieldOption
                  key={field.id}
                  field={field}
                  handleToggleField={handleToggleField}
                />
              ))}
          </Stack>
        </>
      )}
    </Stack>
  );
};

export default ViewSettingsFields;
